<script setup lang="ts">
  import AttendanceProgressBar from '@/components/attendance/AttendanceProgressBar.vue';
  import { useApi } from '@/composables/use-api.ts';
  import { AttendanceDto } from '@/services/api/attendance-day-api.ts';
  import {
    AttendanceMonthDto,
    AttendanceMonthSummary,
  } from '@/services/api/attendance-month-api.ts';
  import { CompanyCalendarBusinessDayDto } from '@/services/api/calendar-day-api.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { useApprovalStore } from '@/stores/approval.ts';
  import { useAttendanceMonthStore } from '@/stores/attendance-month.ts';
  import { useAuthStore } from '@/stores/auth.ts';
  import { useThemeStore } from '@/stores/theme';
  import { secondsToHoursAndMinutes } from '@/utils/item-printer.ts';
  import dayjs from 'dayjs';
  import { computed, onMounted, ref, watch } from 'vue';

  const { attendance, company } = useApi();
  const authStore = useAuthStore();
  const attendanceMonthStore = useAttendanceMonthStore();
  const approvalStore = useApprovalStore();
  const themeStore = useThemeStore();

  const loading = ref<boolean>(false);
  const attendanceMonth = ref<AttendanceMonthDto>();
  const attendanceSummary = ref<AttendanceMonthSummary>();
  const pendingApplicationCnt = ref<number>(0);
  const businessDay = ref<CompanyCalendarBusinessDayDto>();
  const attendanceDto = ref<AttendanceDto>();
  const vacationCountAfterToday = ref<number>();

  const props = defineProps<{
    refresh: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'on-change'): void;
  }>();

  watch(
    () => props.refresh,
    (newValue) => {
      if (newValue) {
        refreshData();
        emit('on-change');
      }
    }
  );

  const hoursWorkedPercent = computed(() => {
    if (!attendanceSummary.value || !attendanceMonth.value) return 0;

    const a = attendanceSummary.value.paidWorkTime / 3600;
    const b = a / attendanceMonth.value.contractWorkHour;
    return Math.round(b * 100) || 0;
  });

  /** 월 추정 근로 시간 */
  const expectedMonthlyWorkTime = computed(() => {
    if (!attendanceSummary.value || !businessDay.value) return 0;
    // 월의 첫날 쉬러가기하면 이 값이 0이 되어 계산 값이 infinity가 됨
    if (businessDay.value.today === 0) return 0;

    const a = attendanceSummary.value.paidWorkTime / businessDay.value?.today;
    return a * businessDay.value?.month;
  });

  /** 유급 연차 */
  const vacationPayTime = computed(
    () => attendanceSummary.value?.vacationPayTime ?? 0
  );

  /** 인정 근로 시간 : 누적 근로 시간 + 연차 + 연차 외 시간의 합계시간 */
  const properWorkTime = computed(() => {
    return (attendanceSummary.value?.paidWorkTime ?? 0) + vacationPayTime.value;
  });

  /** 잔여시간 : 의무근로시간 - 인정근로시간 */
  const workingHoursRemained = computed(() => {
    if (!attendanceSummary.value || !attendanceMonth.value) return 0;

    const contracted = attendanceMonth.value.contractWorkHour * 3600; // 소정 근로 시간
    const remained =
      contracted - properWorkTime.value < 0
        ? 0
        : contracted - properWorkTime.value;

    return secondsToHoursAndMinutes(remained);
  });

  /** 잔여일 : 월 기준 영업일 - (오늘 기준 영업일 + 도래할 휴가 일수) */
  const remainedDay = computed(() => {
    if (!attendanceSummary.value || !businessDay.value) return 0;

    return (
      businessDay.value?.month -
      (businessDay.value?.today + (vacationCountAfterToday?.value ?? 0))
    );
  });

  /** 잔여일 평균 근로시간 : 의무근로시간 - 인정근로시간 / 잔여일 */
  const remainedWorkingHourDailyAverage = computed(() => {
    if (
      !attendanceSummary.value ||
      !attendanceMonth.value ||
      !businessDay.value
    )
      return 0;

    const contracted = attendanceMonth.value.contractWorkHour * 3600; // 소정 근로 시간
    const remained =
      contracted - properWorkTime.value < 0
        ? 0
        : contracted - properWorkTime.value;
    const remainedDay =
      businessDay.value?.month -
      (businessDay.value?.today + (vacationCountAfterToday?.value ?? 0));

    return secondsToHoursAndMinutes(
      remainedDay == 0 ? 0
	      : (remained / (remainedDay < 1 ? 1 : remainedDay))
    );
  });

  /** 최대 근로 시간 */
  const maxCanWorkTime = computed(() => {
    return (
      (attendanceMonth.value?.statutoryWorkHour ?? 0) +
      (attendanceMonth.value?.maxExtraWorkHour ?? 0)
    );
  });

  /** 데이터 새로고침 */
  async function refreshData() {
    const ym = dayjs().format('YYYY-MM');
    const today = dayjs().format('YYYY-MM-DD');
    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        attendanceDto.value = await attendance.day.getAsync(
          authStore.user.employeeNo,
          today
        );

        attendanceMonth.value = await attendanceMonthStore.getAsync(ym);
        if (!attendanceMonth.value) return;

        attendanceSummary.value = await attendance.month.getSummaryAsync(
          attendanceMonth.value.ccmId
        );

        vacationCountAfterToday.value =
          await attendance.day.getCountOfVacationAfterTodayAsync(ym);

        businessDay.value = await company.calendarDay.businessDayCountAsync(ym);
        if (!businessDay.value) businessDay.value = { month: 0, today: 0 };
        else {
          if (
            !attendanceDto.value?.vacationType &&
            !attendanceDto.value?.workEndDt
          ) {
            businessDay.value = {
              month: businessDay.value?.month,
              today: businessDay.value?.today - 1,
            };
          }
        }

        if (authStore.isManager) {
          const items = await approvalStore.getMyPendingApprovalApplications();
          pendingApplicationCnt.value = items.length;
        }
      }
    );
  }

  onMounted(() => {
    refreshData();
  });
</script>

<template>
  <skeleton
    v-if="loading"
    class="my-5" />
  <div v-else>
    <h3 class="text-md font-bold ml-1 pb-1">
      {{ dayjs().format('M월') }} 근무 요약
    </h3>

    <div class="flex justify-center mt-1">
      <div
        class="stats border shadow mb-4 w-full"
        :class="{
          'border-neutral': themeStore.isDark,
        }">
        <div class="stat place-items-center p-3">
          <div class="stat-title text-sm">의무 근로 시간</div>
          <div class="stat-value text-lg">
            {{ attendanceMonth?.contractWorkHour }}시간
          </div>
        </div>

        <div class="stat place-items-center px-3 py-3">
          <div class="stat-title text-sm">최대 근로 시간</div>
          <div class="stat-value text-lg">{{ maxCanWorkTime }}시간</div>
        </div>
      </div>
    </div>

    <AttendanceProgressBar
      v-if="attendanceMonth?.id && attendanceSummary?.paidWorkTime"
      :attendance-month="attendanceMonth"
      :work-time="attendanceSummary?.paidWorkTime ?? 0"
      :vacation-time="attendanceSummary?.vacationPayTime ?? 0"
      class="mb-3" />

    <div class="px-2 pb-3 text-sm leading-7">
      <p>
        실제 근로 시간
        <span class="float-right">
          <span class="text-secondary font-bold">
            {{ secondsToHoursAndMinutes(attendanceSummary?.paidWorkTime ?? 0) }}
          </span>
          /
          <span class="font-bold">
            {{ attendanceMonth?.contractWorkHour }}시간
          </span>
          <span class="ml-1 font-bold badge badge-secondary text-white">
            {{ hoursWorkedPercent }}%
          </span>
        </span>
      </p>
      <p>
        월 추정 근로 시간
        <span class="float-right">
          {{ secondsToHoursAndMinutes(expectedMonthlyWorkTime) }}
        </span>
        <TooltipIcon text="현재 페이스 기준 월 근로 예상시간" />
      </p>
      <p>
        휴가시간
        <span class="float-right">
          {{ secondsToHoursAndMinutes(vacationPayTime) }}
        </span>
      </p>
      <p>
        인정 근로 시간
        <span class="float-right">
          {{ secondsToHoursAndMinutes(properWorkTime) }}
        </span>
      </p>
      <p>
        잔여 시간
        <span class="float-right">{{ workingHoursRemained }}</span>
      </p>
      <p>
        잔여일
        <span class="float-right">{{ remainedDay }}일</span>
      </p>
      <p>
        잔여일 평균 근로시간
        <span class="float-right">{{ remainedWorkingHourDailyAverage }}</span>
        <TooltipIcon
          text="의무근로시간 도달을 위한 잔여시간 일 평균 근로시간" />
      </p>

      <p v-if="authStore.isManager">
        승인할 신청서
        <span
          v-if="pendingApplicationCnt != 0"
          class="ml-1 font-bold badge badge-secondary text-white float-right">
          {{ pendingApplicationCnt }} 개
        </span>
        <span
          v-else
          class="float-right">
          없음
        </span>
      </p>
    </div>
  </div>
</template>
